import React, { useEffect, useMemo, useState } from "react";
import { Box, Collapse, Stack, Typography } from "@mui/material";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { useForm } from "react-hook-form";
import {
  CustomModal,
  CustomDrawer,
  CustomTextInput,
  CustomSingleSelect,
  CustomCategorySelect,
  CustomFileUploader,
  CustomButton,
  CustomCheckbox,
  CustomSectionTitle,
  FixedActionBar,
  colors,
  useScrollToFirstError,
  usePreviewDrawerUnsaved,
  type CategoryOption,
} from "@ippon/shared-components";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store";
import {
  fetchRelatedLawManagement,
  upsertRelatedLawManagement,
} from "@/store/relatedLawManagementSlice";
import type {
  ManagementTaskRecord,
  CreateManagementTaskBody,
  UpdateManagementTaskBody,
  LinkedUnit,
  ManagementTaskType,
  ManagementTaskFrequency,
  ManagementTaskFrequencyUnit,
  ManagementTaskTargetArea,
} from "@/store/managementTaskSlice";
import { type CustomFieldValue } from "./CustomFieldRenderer";
import ManagementTaskCustomFieldsAddable, {
  type PendingManagementCustomField,
} from "./ManagementTaskCustomFieldsAddable";
import { createManagementTaskCustomField } from "@/store/managementTaskCustomFieldSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { Can } from "@/components/auth/Can";

interface FormValues {
  name: string;
  task_type: ManagementTaskType | "";
  description: string;
  related_laws_input: string;
  frequency: ManagementTaskFrequency;
  frequency_count: string;
  frequency_unit: ManagementTaskFrequencyUnit;
  target_areas: ManagementTaskTargetArea[];
  linked_units: string[];
  contractor: string;
  remarks: string;
  attachments: unknown[];
  /** 法定点検フラグ。task_type === "点検" のときだけ UI に出して送信する */
  is_legal_inspection: boolean;
}

const TYPE_OPTIONS: { value: ManagementTaskType; label: string }[] = [
  { value: "清掃", label: "清掃" },
  { value: "点検", label: "点検" },
  { value: "検査", label: "検査" },
  { value: "調査", label: "調査" },
  { value: "業務", label: "業務" },
  { value: "試験", label: "試験" },
  { value: "その他", label: "その他" },
];

const FREQ_OPTIONS: { value: ManagementTaskFrequency; label: string }[] = [
  { value: "daily", label: "日" },
  { value: "weekly", label: "週" },
  { value: "monthly", label: "月" },
  { value: "quarterly", label: "四半期" },
  { value: "annual", label: "年" },
  { value: "always", label: "常時" },
];

// frequency_count のバリデーションは「数字 + Not null」のみで、
// 頻度ごとの上限 (旧 FREQUENCY_COUNT_MAX) は廃止 (2026-05)。

/** 頻度から自動導出する (回数の) 単位とラベル */
const deriveFrequencyUnit = (
  frequency: ManagementTaskFrequency,
): { unit: ManagementTaskFrequencyUnit; suffix: string } | null => {
  switch (frequency) {
    case "daily":
      // 日に N 回 → 単位は「週」「月」「年」と組合せず、内部値は「週」固定で良い
      // (BE は frequency=daily を見るため frequency_unit の値は意味を持たない)
      return { unit: "週", suffix: "回 / 日" };
    case "weekly":
      return { unit: "週", suffix: "回 / 週" };
    case "monthly":
      return { unit: "月", suffix: "回 / 月" };
    case "quarterly":
      return { unit: "年", suffix: "回 / 四半期" };
    case "annual":
      return { unit: "年", suffix: "回 / 年" };
    case "always":
    default:
      return null;
  }
};

const TARGET_AREA_OPTIONS: { value: ManagementTaskTargetArea; label: string }[] =
  [
    { value: "専有部", label: "専有部" },
    { value: "共用部", label: "共用部" },
    { value: "建物設備", label: "建物設備" },
    { value: "敷地", label: "敷地" },
    { value: "その他", label: "その他" },
  ];

const RELATED_LAW_PRESETS: CategoryOption[] = [
  { label: "建築基準法", value: "建築基準法" },
  { label: "消防法", value: "消防法" },
  { label: "電気事業法", value: "電気事業法" },
  { label: "水道法", value: "水道法" },
  { label: "下水道法", value: "下水道法" },
  { label: "労働安全衛生法", value: "労働安全衛生法" },
];

const AREA_TYPE_TO_TARGET: Record<string, ManagementTaskTargetArea> = {
  private: "専有部",
  common: "共用部",
  facility: "建物設備",
  land: "敷地",
  other: "その他",
};

interface Props {
  open: boolean;
  onClose: () => void;
  task: ManagementTaskRecord | null;
  property_id: string;
  /** 新規登録時の task_type 初期値 (CardTab 選択中の種別)。"" で空白 */
  defaultTaskType?: ManagementTaskType | "";
  /**
   * "modal" 新規登録時 (CustomModal の registerModal)
   * "drawer" 旧編集 drawer (CustomDrawer)
   * "page" /management-tasks/:id ページ表示 (PreviewDrawer 埋め込みも兼用)
   */
  displayMode?: "modal" | "drawer" | "page";
  /** displayMode="page" 時に PreviewDrawer 等に埋め込まれている場合 true (FixedActionBar 追従) */
  embedded?: boolean;
  onSaveExisting: (
    task_id: string,
    body: UpdateManagementTaskBody,
  ) => Promise<void>;
  onSaveNew: (body: CreateManagementTaskBody) => Promise<void>;
}

const normalizeLocalValue = (value: CustomFieldValue["value"]) => {
  if (value == null) return "";
  if (Array.isArray(value)) return [...value].sort();
  return value;
};

const isEmptyLocalValue = (value: ReturnType<typeof normalizeLocalValue>) => {
  if (Array.isArray(value)) return value.length === 0;
  return value === "";
};

const buildLocalStateSnapshot = ({
  selectedRelatedLaws,
  customFieldValues,
  pendingCustomFields,
}: {
  selectedRelatedLaws: string[];
  customFieldValues: CustomFieldValue[];
  pendingCustomFields: PendingManagementCustomField[];
}) =>
  JSON.stringify({
    selectedRelatedLaws: [...selectedRelatedLaws].sort(),
    customFieldValues: customFieldValues
      .map((v) => ({
        field_id: v.field_id,
        value: normalizeLocalValue(v.value),
      }))
      .filter((v) => !isEmptyLocalValue(v.value))
      .sort((a, b) => a.field_id.localeCompare(b.field_id)),
    pendingCustomFields: pendingCustomFields
      .map((v) => ({
        temp_id: v.temp_id,
        label: v.label.trim(),
        value: v.value,
      }))
      .filter((v) => v.label || v.value)
      .sort((a, b) => a.temp_id.localeCompare(b.temp_id)),
  });

const ManagementTaskEditModal: React.FC<Props> = ({
  open,
  onClose,
  task,
  property_id,
  defaultTaskType = "",
  displayMode = "modal",
  embedded = false,
  onSaveExisting,
  onSaveNew,
}) => {
  const isEdit = !!task;
  const dispatch = useDispatch<AppDispatch>();
  const units = useSelector((s: RootState) => s.units.list.data);
  // 関連法令マスタ (顧客横断のプリセット拡張)。fetch だけしておき、
  // 「ユーザー追加 → ローカルだけ反映 → submit 成功後にまとめて upsert」する。
  const relatedLawRecord = useSelector(
    (s: RootState) => s.relatedLawManagement.record,
  );
  const [customFieldValues, setCustomFieldValues] = useState<CustomFieldValue[]>(
    [],
  );
  const [pendingCustomFields, setPendingCustomFields] = useState<
    PendingManagementCustomField[]
  >([]);
  const [initialLocalStateSnapshot, setInitialLocalStateSnapshot] =
    useState("");

  const { showToast } = useGlobalToast();
  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      name: "",
      task_type: "",
      description: "",
      related_laws_input: "",
      frequency: "monthly",
      frequency_count: "1",
      frequency_unit: "月",
      target_areas: [],
      linked_units: [],
      contractor: "",
      remarks: "",
      attachments: [],
      is_legal_inspection: false,
    },
  });

  const [relatedLawOptions, setRelatedLawOptions] = useState<CategoryOption[]>(
    RELATED_LAW_PRESETS,
  );
  const [selectedRelatedLaws, setSelectedRelatedLaws] = useState<string[]>([]);
  const localStateSnapshot = useMemo(
    () =>
      buildLocalStateSnapshot({
        selectedRelatedLaws,
        customFieldValues,
        pendingCustomFields,
      }),
    [selectedRelatedLaws, customFieldValues, pendingCustomFields],
  );
  const localStateDirty =
    !!initialLocalStateSnapshot &&
    localStateSnapshot !== initialLocalStateSnapshot;
  const hasUnsavedChanges = isDirty || localStateDirty;

  useEffect(() => {
    if (!open) return;
    const initialLaws = task?.related_laws ?? [];
    reset({
      name: task?.name || "",
      task_type: task?.task_type || defaultTaskType,
      description: task?.description || "",
      related_laws_input: "",
      frequency: task?.frequency || "monthly",
      frequency_count: String(task?.frequency_count ?? 1),
      frequency_unit: (task?.frequency_unit as ManagementTaskFrequencyUnit) || "月",
      target_areas: (task?.target_areas as ManagementTaskTargetArea[]) || [],
      linked_units: (task?.linked_units || []).map((u) => u.unit_id),
      contractor: task?.contractor || "",
      remarks: task?.remarks || "",
      attachments: (task?.attachments || []) as unknown[],
      is_legal_inspection: !!task?.is_legal_inspection,
    });
    setSelectedRelatedLaws(initialLaws);
  }, [open, task, reset, defaultTaskType]);

  // モーダルを開いた時点で 1 度だけ関連法令マスタを fetch (顧客横断プリセット拡張用)。
  useEffect(() => {
    if (!open) return;
    dispatch(fetchRelatedLawManagement()).catch(() => {});
  }, [open, dispatch]);

  // カスタムフィールドの初期化は「モーダルを開いた瞬間 + task が切り替わった瞬間」だけ。
  // 関連法令マスタ (relatedLawRecord) の非同期 fetch 完了でこの effect が再実行されると、
  // 入力途中の customFields がリセットされてしまうため、関連法令 options の rebuild とは
  // effect を分離する。
  useEffect(() => {
    if (!open) return;
    const initialLaws = task?.related_laws ?? [];
    const initialCustomFieldValues = (task?.custom_fields || []).map((c) => ({
      field_id: c.field_id,
      value: c.value as never,
    }));
    setCustomFieldValues(
      initialCustomFieldValues,
    );
    // モーダルを開き直すたびに pending はリセット (前回入力中の項目を引き継がない)
    setPendingCustomFields([]);
    setInitialLocalStateSnapshot(
      buildLocalStateSnapshot({
        selectedRelatedLaws: initialLaws,
        customFieldValues: initialCustomFieldValues,
        pendingCustomFields: [],
      }),
    );
  }, [open, task]);

  // 関連法令 options の rebuild は customFields とは独立。
  // RELATED_LAW_PRESETS (プリセット) + 関連法令マスタ (顧客追加分) + task に既に紐付いている法令
  // + ユーザーがこのセッションで選択/追加した法令 (selectedRelatedLaws) をマージする。
  // selectedRelatedLaws を含めることで、BE fetch 完了の rebuild でユーザーが直前に
  // ローカル追加した法令 (= まだ relatedLawRecord に載っていない) が options から消えるのを防ぐ。
  useEffect(() => {
    if (!open) return;
    const initialLaws = task?.related_laws ?? [];
    const beCustom: string[] = Array.isArray(relatedLawRecord?.related_laws)
      ? relatedLawRecord.related_laws
      : [];
    const merged = Array.from(
      new Set([
        ...RELATED_LAW_PRESETS.map((o) => o.value),
        ...beCustom,
        ...initialLaws,
        ...selectedRelatedLaws,
      ]),
    );
    setRelatedLawOptions(merged.map((v) => ({ label: v, value: v })));
  }, [open, task, relatedLawRecord, selectedRelatedLaws]);

  const watchedTaskType = watch("task_type");
  const watchedFrequency = watch("frequency");
  const watchedTargetAreas = watch("target_areas");

  // 法定点検は task レベルへ集約 (= 「点検」業務でのみチェックボックスを出す)。
  // リモートのみ月別ドロワー側で「予定/実績」別に保持する。
  const showFrequencyCount = watchedFrequency !== "always";
  const showLegalInspection = watchedTaskType === "点検";
  const watchedIsLegalInspection = watch("is_legal_inspection");

  // 種別が「点検」以外に切り替わったら法定フラグを自動で外す (送信時にも 0 化するが、
  // UI 上のチェック状態も同期しておく)。
  useEffect(() => {
    if (watchedTaskType !== "点検" && watchedIsLegalInspection) {
      setValue("is_legal_inspection", false, { shouldDirty: true });
    }
  }, [watchedTaskType, watchedIsLegalInspection, setValue]);

  const linkedAssetOptions = useMemo<CategoryOption[]>(() => {
    const allowed = new Set(watchedTargetAreas || []);
    return units
      .filter((u) => {
        const target = AREA_TYPE_TO_TARGET[u.area_type];
        if (!target) return false;
        if (allowed.size === 0) return false;
        return allowed.has(target);
      })
      .map((u) => ({
        label: `${u.landmark_name}(${
          AREA_TYPE_TO_TARGET[u.area_type] || u.area_type
        })`,
        value: u.unit_id,
      }));
  }, [units, watchedTargetAreas]);

  // 場所区分が変更された時に、選択中の名称 (linked_units) のうち新しい
  // target_areas で許容されなくなった unit_id を取り除くハンドラ。
  // 旧実装は useEffect で linkedAssetOptions の変化を監視していたが、
  // 初回 open 時のレース (closure が空の linkedAssetOptions を掴んだまま
  // reset 直後に走ると保存値を全消去する) があり、open 時に値が消えていた。
  const handleTargetAreasChange = (next: string[]) => {
    setValue("target_areas", next as ManagementTaskTargetArea[], {
      shouldDirty: true,
    });
    const allowedAreas = new Set(next);
    const stillAllowedIds = new Set(
      units
        .filter((u) => {
          const target = AREA_TYPE_TO_TARGET[u.area_type];
          return !!target && allowedAreas.has(target);
        })
        .map((u) => u.unit_id),
    );
    const currentLinked = watch("linked_units") || [];
    const pruned = currentLinked.filter((id) => stillAllowedIds.has(id));
    if (pruned.length !== currentLinked.length) {
      setValue("linked_units", pruned, { shouldDirty: true });
    }
  };

  // 頻度変化時に frequency_unit を自動マッピング (ユーザーには見せない)
  useEffect(() => {
    const derived = deriveFrequencyUnit(watchedFrequency);
    if (derived && watch("frequency_unit") !== derived.unit) {
      setValue("frequency_unit", derived.unit, { shouldDirty: false });
    }
  }, [watchedFrequency, setValue, watch]);

  const handleAddRelatedLaw = (value: string) => {
    const v = value.trim();
    if (!v) return;
    if (!relatedLawOptions.some((o) => o.value === v)) {
      setRelatedLawOptions((prev) => [...prev, { label: v, value: v }]);
    }
    if (!selectedRelatedLaws.includes(v)) {
      setSelectedRelatedLaws((prev) => [...prev, v]);
    }
  };
  const handleDeleteRelatedLawOption = (value: string) => {
    setRelatedLawOptions((prev) => prev.filter((o) => o.value !== value));
    setSelectedRelatedLaws((prev) => prev.filter((v) => v !== value));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const scrollToFirstError = useScrollToFirstError<FormValues>();

  const onSubmit = handleSubmit(async (v) => {
    setIsSubmitting(true);
    try {
      if (!v.task_type) {
        // 種別未選択の場合は弾く (BE 必須)。CustomSingleSelect の error 表示は
        // useForm の rules で出すのが本筋だが、簡易対応で submit ボタン時に弾く。
        return;
      }

      // 回数: 「数字 + Not null」のみチェック。上限・下限は廃止 (2026-05)。
      // 入力欄は type="number" のため非数字は browser 側で弾かれ、value は空になる。
      // empty / non-numeric を統一して「数字のみ入力可能です」で示す。
      const requiresCount = v.frequency !== "always";
      let safeCount: number | undefined;
      if (requiresCount) {
        const n = Number(v.frequency_count);
        if (
          v.frequency_count == null ||
          v.frequency_count === "" ||
          !Number.isFinite(n)
        ) {
          setError("frequency_count", {
            type: "manual",
            message: "数字のみ入力可能です",
          });
          return;
        }
        clearErrors("frequency_count");
        safeCount = Math.floor(n);
      }

      const decodedUnits: LinkedUnit[] = v.linked_units
        .map((id) => units.find((u) => u.unit_id === id))
        .filter((u): u is NonNullable<typeof u> => Boolean(u))
        .map((u) => ({
          unit_id: u.unit_id,
          area_type: u.area_type,
          name: u.landmark_name,
        }));

      // pending fields の flush: master upsert → 戻りの field_id を custom_fields にマージ。
      // 失敗時は record save も実行しない。
      let mergedCustomFields = customFieldValues;
      if (pendingCustomFields.length > 0) {
        const results = await Promise.all(
          pendingCustomFields.map((p) =>
            dispatch(
              createManagementTaskCustomField({
                label: p.label,
                field_type: "text",
                section_key: "task",
                applies_to_task_types: v.task_type
                  ? [v.task_type as ManagementTaskType]
                  : undefined,
              }),
            ),
          ),
        );
        const failed = results.find(
          (r) => !createManagementTaskCustomField.fulfilled.match(r),
        );
        if (failed) {
          const msg =
            (failed.payload as string) ||
            "カスタムフォーム項目の追加に失敗しました";
          showToast({ type: "error", message: msg });
          return;
        }
        const newValues: CustomFieldValue[] = results.map((r, i) => ({
          field_id: (r.payload as { field_id: string }).field_id,
          value: pendingCustomFields[i].value as never,
        }));
        mergedCustomFields = [...mergedCustomFields, ...newValues];
        setPendingCustomFields([]);
      }

      const base = {
        name: v.name.trim(),
        task_type: v.task_type as ManagementTaskType,
        frequency: v.frequency,
        frequency_count: safeCount,
        frequency_unit: requiresCount ? v.frequency_unit : undefined,
        description: v.description.trim() || undefined,
        related_laws: selectedRelatedLaws,
        contractor: v.contractor.trim() || undefined,
        target_areas: v.target_areas,
        linked_units: decodedUnits,
        remarks: v.remarks.trim() || undefined,
        custom_fields: mergedCustomFields,
        // 法定点検は「点検」業務でのみ有意。種別が違えば常に false で送って残骸を残さない。
        is_legal_inspection:
          v.task_type === "点検" ? !!v.is_legal_inspection : false,
      };

      try {
        if (isEdit && task) {
          await onSaveExisting(task.task_id, base);
        } else {
          await onSaveNew({ property_id, ...base });
        }

        // 管理業務本体の保存に成功 → 関連法令で新規追加された値があれば
        // ここで初めて relatedLawManagement に upsert する (ローカル追加分の遅延永続化)。
        // プリセット (RELATED_LAW_PRESETS) は対象外。
        const presetValues = new Set(
          RELATED_LAW_PRESETS.map((o) => o.value),
        );
        const beCustom: string[] = Array.isArray(
          relatedLawRecord?.related_laws,
        )
          ? relatedLawRecord.related_laws
          : [];
        const localCustom = relatedLawOptions
          .map((o) => o.value)
          .filter((v) => !presetValues.has(v));
        const hasNewLaw = localCustom.some((v) => !beCustom.includes(v));
        if (hasNewLaw) {
          const merged = Array.from(new Set([...beCustom, ...localCustom]));
          dispatch(
            upsertRelatedLawManagement({
              existingId: relatedLawRecord?.related_law_management_id,
              related_laws: merged,
            }),
          ).catch(() => {});
        }

        onClose();
      } catch (e) {
        // API エラーはトーストで表示し、モーダルは閉じない (修正の機会を残す)
        const message =
          (e instanceof Error && e.message) ||
          (typeof e === "string" ? e : "") ||
          "保存に失敗しました";
        showToast({ type: "error", message });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, scrollToFirstError);

  // PreviewDrawer (点検/清掃の月別/詳細プレビュー) に埋め込まれた page モード時、
  // 編集中(dirty)を親 PreviewDrawer に報告し、閉じ操作で破棄確認を出させる。
  // PreviewDrawer 外 (modal モード等) では no-op。
  usePreviewDrawerUnsaved(hasUnsavedChanges);

  const [unsavedConfirmOpen, setUnsavedConfirmOpen] = useState(false);
  const requestClose = () => {
    if (hasUnsavedChanges) {
      setUnsavedConfirmOpen(true);
      return;
    }
    onClose();
  };
  const confirmDiscardAndClose = () => {
    setUnsavedConfirmOpen(false);
    onClose();
  };

  const title = isEdit ? "管理業務編集" : "管理業務登録";
  // NOTE: registerModal の CSS は `.modalBoxWrapper` 直下の flex 子として
  // `.registerModalBody` (スクロール) と `.registerModalFooter` (固定) が
  // 並ぶ前提。外側に div を 1 段挟むとスクロール領域とフッタが両方崩れるため、
  // testid は body 側に付与する。
  const formBody = (
    <>
      <Box
        data-testid="management-task-edit-modal"
        className={
          displayMode === "modal" ? "registerModalBody" : undefined
        }
        sx={displayMode === "drawer" ? { p: 0 } : undefined}
      >
        <Stack spacing={1}>
          <Box>
            <Box sx={{ mb: 2 }}>
              <CustomSectionTitle title="基本情報" size="small" addBorder />
            </Box>
            <Stack spacing={0}>
              <Box data-testid="management-task-input-name">
                <CustomTextInput
                  label="項目名"
                  name="name"
                  register={register}
                  errors={errors}
                  isRequired
                  placeholder="例: 共用部清掃"
                />
              </Box>
              <Box data-testid="management-task-input-task-type">
                <CustomSingleSelect
                  label="種別"
                  name="task_type"
                  control={control}
                  errors={errors}
                  isRequired
                  options={TYPE_OPTIONS}
                  inputWidth="100%"
                  inputWidthSp="100%"
                />
              </Box>
              {showLegalInspection && (
                <Box
                  data-testid="management-task-input-is-legal-inspection"
                  sx={{ mb: 1.5 }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CustomCheckbox
                      checked={!!watchedIsLegalInspection}
                      onChange={() =>
                        setValue("is_legal_inspection", !watchedIsLegalInspection, {
                          shouldDirty: true,
                        })
                      }
                    />
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: colors.text.primary,
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                      onClick={() =>
                        setValue("is_legal_inspection", !watchedIsLegalInspection, {
                          shouldDirty: true,
                        })
                      }
                    >
                      法定点検
                    </Typography>
                  </Stack>
                </Box>
              )}
              <Box>
                <CustomTextInput
                  label="概要説明"
                  name="description"
                  register={register}
                  errors={errors}
                  multiline
                  rows={3}
                />
              </Box>
              <Box data-testid="management-task-input-related-laws">
                <CustomCategorySelect
                  label="関連法令"
                  name="related_laws"
                  options={relatedLawOptions}
                  selectedCategories={selectedRelatedLaws}
                  onCategoriesChange={setSelectedRelatedLaws}
                  onAddNewCategory={handleAddRelatedLaw}
                  onDeleteCategoryOption={handleDeleteRelatedLawOption}
                  errors={errors}
                  inputWidth="100%"
                  placeholder="関連法令を選択 / 自由入力"
                />
              </Box>
              <Box data-testid="management-task-input-frequency">
                <CustomSingleSelect
                  label="頻度"
                  name="frequency"
                  control={control}
                  errors={errors}
                  isRequired
                  options={FREQ_OPTIONS}
                  inputWidth="100%"
                  inputWidthSp="100%"
                />
              </Box>
              {showFrequencyCount && (
                <Box
                  sx={{ ml: 3, position: "relative" }}
                  data-testid="management-task-input-frequency-count-row"
                >
                  <SubdirectoryArrowRightIcon
                    sx={{
                      position: "absolute",
                      left: -20,
                      top: 1,
                      fontSize: 16,
                      color: "#989898",
                      pointerEvents: "none",
                    }}
                  />
                  <CustomTextInput
                    label="回数"
                    name="frequency_count"
                    register={register}
                    errors={errors}
                    type="number"
                    isRequired
                    requiredMessage="数字のみ入力可能です"
                    endAdornmentText={
                      deriveFrequencyUnit(watchedFrequency)?.suffix || ""
                    }
                  />
                </Box>
              )}
              <Box data-testid="management-task-input-target-areas">
                <CustomCategorySelect
                  label="場所区分"
                  name="target_areas"
                  options={TARGET_AREA_OPTIONS}
                  selectedCategories={watchedTargetAreas || []}
                  onCategoriesChange={handleTargetAreasChange}
                  errors={errors}
                  inputWidth="100%"
                  placeholder="場所区分を選択"
                  hideSearch
                />
              </Box>
              <Collapse
                in={(watchedTargetAreas?.length ?? 0) > 0}
                timeout={400}
              >
                <Box
                  data-testid="management-task-input-linked-units"
                  sx={{
                    ml: 3,
                    position: "relative",
                    transition: "all 0.4s ease-out",
                    transform: (watchedTargetAreas?.length ?? 0) > 0
                      ? "translateX(0)"
                      : "translateX(-20px)",
                    opacity: (watchedTargetAreas?.length ?? 0) > 0 ? 1 : 0,
                  }}
                >
                  <SubdirectoryArrowRightIcon
                    sx={{
                      position: "absolute",
                      left: -20,
                      top: 0,
                      fontSize: 16,
                      color: "#989898",
                      pointerEvents: "none",
                    }}
                  />
                  <CustomCategorySelect
                    label="名称"
                    name="linked_units"
                    options={linkedAssetOptions}
                    selectedCategories={watch("linked_units") || []}
                    onCategoriesChange={(next) =>
                      setValue("linked_units", next, { shouldDirty: true })
                    }
                    errors={errors}
                    inputWidth="100%"
                    placeholder={
                      linkedAssetOptions.length === 0
                        ? "対象の名称がありません"
                        : "名称を選択"
                    }
                  />
                </Box>
              </Collapse>
              <Box>
                <CustomTextInput
                  label="対応業者"
                  name="contractor"
                  register={register}
                  errors={errors}
                />
              </Box>
              <Box>
                <CustomTextInput
                  label="備考"
                  name="remarks"
                  register={register}
                  errors={errors}
                  multiline
                  rows={3}
                />
              </Box>
              <Box
                data-testid="management-task-input-attachments"
                sx={{
                  "& .MuiTypography-root": { mt: "0 !important" },
                }}
              >
                <CustomFileUploader
                  label="添付資料"
                  name="attachments"
                  setValue={setValue as any}
                  register={register as any}
                  initialImages={[]}
                />
              </Box>
              <Box sx={{ my: 2 }}>
                <ManagementTaskCustomFieldsAddable
                  section="task"
                  taskType={watchedTaskType || undefined}
                  values={customFieldValues}
                  onChange={(next) => {
                    setCustomFieldValues(next);
                  }}
                  pendingFields={pendingCustomFields}
                  onPendingChange={(next) => {
                    setPendingCustomFields(next);
                  }}
                />
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Box>
      {/* modal 表示時のみ formBody 内部にフッターを置く。
          drawer 表示時は CustomDrawer の footerContent (下部固定) に出す。 */}
      {displayMode === "modal" && (
        <Box className="registerModalFooter">
          <Box data-testid="management-task-cancel-button">
            <CustomButton
              label="戻る"
              buttonCategory="cancelModal"
              onClick={requestClose}
              disabled={isSubmitting}
              sx={{ minWidth: "140px !important", width: "140px" }}
            />
          </Box>
          <Can action="management-task:edit">
            <Box data-testid="management-task-save-button">
              <CustomButton
                label={isEdit ? "更新" : "登録"}
                onClick={onSubmit}
                buttonCategory="submit"
                isLoading={isSubmitting}
                disabled={isSubmitting || !hasUnsavedChanges}
                sx={{ minWidth: "140px !important", width: "140px" }}
              />
            </Box>
          </Can>
        </Box>
      )}
    </>
  );

  // ManagementResultEditDrawer と同じく下部固定のフッターに置く。
  const drawerFooter = (
    <Stack
      direction="row"
      justifyContent="center"
      sx={{ py: "0px" }}
      data-testid="management-task-save-button"
    >
      <Can action="management-task:edit">
        <CustomButton
          label={isEdit ? "更新" : "登録"}
          onClick={onSubmit}
          buttonCategory="submit"
          isLoading={isSubmitting}
          disabled={isSubmitting || !hasUnsavedChanges}
        />
      </Can>
    </Stack>
  );

  const unsavedConfirmModal = (
    <CustomModal
      openModal={unsavedConfirmOpen}
      handleCloseModal={() => setUnsavedConfirmOpen(false)}
      title="ページ移動の確認"
      bodyText="入力中の情報が失われますが、よろしいでしょうか？"
      modalType="deleteModal"
      bodyTextAlign="left"
      sx={{ padding: "0px !important" }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", gap: "10px" }}>
        <CustomButton
          label="戻る"
          buttonCategory="cancelModal"
          onClick={() => setUnsavedConfirmOpen(false)}
          sx={{ width: "50% !important" }}
        />
        <CustomButton
          label="移動"
          buttonCategory="confirm"
          onClick={confirmDiscardAndClose}
          sx={{ width: "50% !important" }}
        />
      </Box>
    </CustomModal>
  );

  if (displayMode === "drawer") {
    return (
      <>
        <CustomDrawer
          open={open}
          onClose={requestClose}
          anchor="right"
          width={720}
          title={title}
          showFooter
          footerContent={drawerFooter}
          sx={{
            "& .drawerFooter": {
              padding: "8px 24px",
              borderTop: "none",
            },
          }}
        >
          {formBody}
        </CustomDrawer>
        {unsavedConfirmModal}
      </>
    );
  }

  // ページ表示: /management-tasks/:id ルートまたは PreviewDrawer 埋め込み。
  // formBody を Modal/Drawer の chrome なしで描画し、アクションは FixedActionBar で出す。
  // 幅制約: 各 input は inputWidth="100%" なので、formBody 内の field Stack (depth 3 の
  // .MuiStack-root) だけ maxWidth: 600 にする。CustomSectionTitle はその外側にいるので
  // border underline は引き続きページ幅まで伸びる。
  if (displayMode === "page") {
    return (
      <>
        <Box
          data-entity="management_task"
          data-id={task?.task_id || ""}
          sx={{
            // depth: page-mode Box > formBody outer Box > Stack(spacing=1) > Section Box
            //        > Stack(spacing=0) [= field Stack]
            "& > * > * > * > .MuiStack-root": { maxWidth: 600 },
          }}
        >
          {formBody}
        </Box>
        <FixedActionBar
          submitLabel={isEdit ? "更新" : "登録"}
          onSubmit={onSubmit}
          isLoading={isSubmitting}
          disabled={isSubmitting || !hasUnsavedChanges}
          embedded={embedded}
        />
        {unsavedConfirmModal}
      </>
    );
  }

  return (
    <>
      <CustomModal
        openModal={open}
        handleCloseModal={requestClose}
        title={title}
        modalType="registerModal"
        addTitleBorder
      >
        {formBody}
      </CustomModal>
      {unsavedConfirmModal}
    </>
  );
};

export default ManagementTaskEditModal;
